import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, mediaUrl } from '../../services/api.js'
import { useTreeStore } from '../../stores/tree.js'

/**
 * OriginResearchHero — prominent dashboard card surfacing recent Origin AI
 * runs the user hasn't reviewed yet. Highest priority: runs with
 * unreviewed proposals (hero slot). Secondary: a single "Try Origin AI"
 * empty-state card encouraging first use.
 *
 * Data: GET /api/research/recent → up to 8 runs with pending_proposals
 * counts and target-person thumbs.
 */
export default function OriginResearchHero() {
  const navigate = useNavigate()
  const { trees, focalPersonData } = useTreeStore()
  const [runs, setRuns] = useState(null)

  useEffect(() => {
    api.get('/research/recent')
      .then(data => setRuns(data.runs || []))
      .catch(() => setRuns([]))
  }, [])

  if (runs === null) return null

  const active = runs.filter(r => r.pending_proposals > 0)
  const completedEmpty = runs.filter(r =>
    r.status === 'completed' && r.pending_proposals === 0
  )
  const inProgress = runs.filter(r => ['queued', 'running'].includes(r.status))

  // Empty state — no runs yet
  if (runs.length === 0) {
    return (
      <div style={emptyStyle}>
        <div style={{ flex: 1 }}>
          <div style={eyebrowStyle}>Origin AI</div>
          <div style={titleStyle}>Let AI research your ancestors</div>
          <p style={leadStyle}>
            Pick any historical person in your tree and Origin will hunt external sources —
            Wikipedia, Dutch newspaper archives, civil registers — for articles, obituaries,
            and missing relatives you've never seen.
          </p>
          <div style={{ display: 'flex', gap: '.6rem', marginTop: '1rem' }}>
            {focalPersonData?.id && focalPersonData?.treeId ? (
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/tree/${focalPersonData.treeId}/person/${focalPersonData.id}`)}
              >Research {focalPersonData.first_name || 'this person'}</button>
            ) : trees?.length > 0 ? (
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/tree/${trees[0].id}`)}
              >Open your tree</button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => navigate('/onboarding')}
              >Start by adding a person</button>
            )}
          </div>
        </div>
        <div aria-hidden style={sealStyle}>🔍</div>
      </div>
    )
  }

  return (
    <div style={wrapperStyle}>
      <div style={headerRowStyle}>
        <div>
          <div style={eyebrowStyle}>Origin AI · research agent</div>
          <div style={titleStyle}>
            {active.length > 0
              ? `${active.length} ${active.length === 1 ? 'investigation has' : 'investigations have'} fresh leads`
              : inProgress.length > 0
                ? 'Origin is investigating…'
                : 'Your recent research'}
          </div>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate('/suggestions')}
          style={{ fontSize: '.85rem' }}
        >
          Open inbox →
        </button>
      </div>

      <div style={gridStyle}>
        {active.slice(0, 3).map(run => (
          <RunCard key={run.id} run={run} navigate={navigate} variant="active" />
        ))}
        {active.length === 0 && inProgress.slice(0, 2).map(run => (
          <RunCard key={run.id} run={run} navigate={navigate} variant="running" />
        ))}
        {active.length === 0 && inProgress.length === 0 && completedEmpty.slice(0, 2).map(run => (
          <RunCard key={run.id} run={run} navigate={navigate} variant="reviewed" />
        ))}
      </div>
    </div>
  )
}

function RunCard({ run, navigate, variant }) {
  const name = [run.first_name, run.last_name].filter(Boolean).join(' ') || 'Unknown person'
  const toPersonPage = () => {
    if (run.tree_id && run.target_person_id) {
      navigate(`/tree/${run.tree_id}/person/${run.target_person_id}`)
    } else {
      navigate('/suggestions')
    }
  }
  const toolStatus = {
    active: { label: `${run.pending_proposals} new lead${run.pending_proposals === 1 ? '' : 's'}`, tone: 'gold' },
    running: { label: run.status === 'running' ? 'Investigating…' : 'Queued', tone: 'green' },
    reviewed: { label: 'Reviewed', tone: 'grey' },
  }[variant]

  return (
    <div style={cardStyle(variant)} onClick={toPersonPage}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.7rem', marginBottom: '.6rem' }}>
        {run.photo_thumb_key ? (
          <img src={mediaUrl(run.photo_thumb_key)} alt={name} style={thumbStyle} />
        ) : (
          <div style={thumbFallbackStyle}>{name.slice(0, 1)}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '.95rem', lineHeight: 1.2 }}>{name}</div>
          <span style={badgeStyle(toolStatus.tone)}>{toolStatus.label}</span>
        </div>
      </div>
      {run.summary_preview && (
        <div style={summaryStyle}>
          {run.summary_preview.length > 200
            ? run.summary_preview.slice(0, 200) + '…'
            : run.summary_preview}
        </div>
      )}
    </div>
  )
}

// ── styles ──

const wrapperStyle = {
  background: '#fff',
  border: '1px solid var(--border, #e5e0d5)',
  borderRadius: 14,
  padding: '1.4rem 1.5rem',
  marginBottom: '1.5rem',
  borderLeft: '3px solid var(--gold, #c8a56e)',
}

const emptyStyle = {
  display: 'flex',
  gap: '1.5rem',
  alignItems: 'center',
  background: 'linear-gradient(135deg, rgba(200,165,110,0.08), rgba(59,107,90,0.04))',
  border: '1px solid var(--border, #e5e0d5)',
  borderRadius: 14,
  padding: '1.8rem 2rem',
  marginBottom: '1.5rem',
}

const eyebrowStyle = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--gold, #c8a56e)',
  marginBottom: '.35rem',
}

const titleStyle = {
  fontFamily: 'var(--font-serif, Georgia, serif)',
  fontSize: '1.4rem',
  fontWeight: 600,
  color: 'var(--text, #1a1a1a)',
  lineHeight: 1.25,
  marginBottom: '.4rem',
}

const leadStyle = {
  fontSize: '0.92rem',
  color: 'var(--text-muted, #5a5a5a)',
  lineHeight: 1.55,
  maxWidth: 640,
  margin: 0,
}

const sealStyle = {
  fontSize: '3rem',
  opacity: 0.3,
}

const headerRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginBottom: '1rem',
  gap: '1rem',
}

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '.8rem',
}

const cardStyle = (variant) => ({
  background: variant === 'active' ? 'rgba(200, 165, 110, .06)' : '#fafaf7',
  border: '1px solid var(--border, #e5e0d5)',
  borderRadius: 10,
  padding: '.9rem 1rem',
  cursor: 'pointer',
  transition: 'transform 150ms, box-shadow 150ms',
})

const thumbStyle = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  objectFit: 'cover',
  flexShrink: 0,
}

const thumbFallbackStyle = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: 'var(--tint, #f0ebe0)',
  color: 'var(--text-muted, #5a5a5a)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1rem',
  fontWeight: 600,
  flexShrink: 0,
}

const badgeStyle = (tone) => ({
  display: 'inline-block',
  marginTop: '.2rem',
  fontSize: '.7rem',
  fontWeight: 600,
  padding: '.1rem .5rem',
  borderRadius: 999,
  background: tone === 'gold'
    ? 'rgba(200,165,110,.18)'
    : tone === 'green'
      ? 'rgba(59,107,90,.12)'
      : 'rgba(120,120,120,.12)',
  color: tone === 'gold'
    ? 'var(--gold, #8a6a10)'
    : tone === 'green'
      ? 'var(--green, #3b6b5a)'
      : 'var(--text-muted, #5a5a5a)',
})

const summaryStyle = {
  fontSize: '0.82rem',
  color: 'var(--text-muted, #5a5a5a)',
  lineHeight: 1.5,
  marginTop: '.3rem',
}
